<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import SettingsPageLayout from 'dashboard/components-next/layout/SettingsPageLayout.vue';
import PermissionGroupForm from './components/PermissionGroupForm.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('permissionGroups/getUIFlags');
const groups = useMapGetter('permissionGroups/getPermissionGroups');

const routeId = computed(() => Number(route.params.id));
const selectedGroup = computed(() =>
  groups.value?.find(g => g.id === routeId.value)
);

// Título do shell: nome do grupo quando carregado, senão o título genérico.
const pageTitle = computed(
  () => selectedGroup.value?.name || t('PERMISSION_GROUPS.EDIT.TITLE')
);

const formData = computed(() => {
  if (!selectedGroup.value) {
    return {
      name: '',
      description: '',
      permissions: [],
      systemDefault: false,
    };
  }
  return {
    name: selectedGroup.value.name || '',
    description: selectedGroup.value.description || '',
    permissions: [...(selectedGroup.value.permissions || [])],
    systemDefault: !!selectedGroup.value.systemDefault,
  };
});

const handleSubmit = async formState => {
  try {
    await store.dispatch('permissionGroups/update', {
      id: routeId.value,
      ...formState,
    });
    useAlert(t('PERMISSION_GROUPS.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('PERMISSION_GROUPS.EDIT.API.ERROR_MESSAGE'));
  }
};

const handleCancel = () => {
  router.push({ name: 'permission_groups_index' });
};

const fetchGroupIfNeeded = () => {
  if (!groups.value || groups.value.length === 0) {
    store.dispatch('permissionGroups/get');
  }
};

watch(routeId, fetchGroupIfNeeded, { immediate: true });
onMounted(fetchGroupIfNeeded);
</script>

<template>
  <SettingsPageLayout
    :title="pageTitle"
    :description="$t('PERMISSION_GROUPS.EDIT.DESCRIPTION')"
    :is-loading="uiFlags.isFetching && !selectedGroup"
    :back-url="{ name: 'permission_groups_index' }"
  >
    <template #body>
      <PermissionGroupForm
        :key="routeId"
        mode="EDIT"
        :initial-data="formData"
        :is-loading="uiFlags.isUpdating"
        @submit="handleSubmit"
        @cancel="handleCancel"
      />
    </template>
  </SettingsPageLayout>
</template>
